import type { Lang } from "@/lib/i18n";

import heroAsset from "@/assets/vision/hero.jpg.asset.json";
import c1Asset from "@/assets/vision/c1.jpg.asset.json";
import c2Asset from "@/assets/vision/c2.jpg.asset.json";
import c3Asset from "@/assets/vision/c3.jpg.asset.json";
import c4Asset from "@/assets/vision/c4.jpg.asset.json";
import c5Asset from "@/assets/vision/c5.jpg.asset.json";

export const VISION_HERO_IMAGE = heroAsset.url;

export const VISION_CARD_IMAGES = [
  c1Asset.url,
  c2Asset.url,
  c3Asset.url,
  c4Asset.url,
  c5Asset.url,
] as const;

export type VisionCard = {
  step: string;
  title: string;
  body: string;
  alt: string;
};

export type VisionCopy = {
  eyebrow: string;
  headline: string;
  subheadline: string;
  heroAlt: string;
  stripEyebrow: string;
  stripTitle: string;
  stripHint: string;
  /** aria-roledescription for the filmstrip container */
  stripRole: string;
  prev: string;
  next: string;
  /** "Card {n} of {total}" — {n}/{total} are replaced at runtime */
  cardPosition: string;
  cards: VisionCard[];
  ctaTitle: string;
  ctaBody: string;
  ctaMeal2: string;
  ctaMeal4: string;
  back: string;
};

const en: VisionCopy = {
  eyebrow: "Vision & Mission",
  headline: "Freezing Food Inflation Worldwide: The $1 Meal Engine.",
  subheadline:
    "How MealToken24, Solana micro-payments, and sustainable infrastructure protect global communities from rising living costs.",
  heroAlt:
    "Global network graphic connecting smart kitchens, solar arrays and sustainable farms across continents",
  stripEyebrow: "Filmstrip",
  stripTitle: "The Global Storyline",
  stripHint: "Use the arrow keys or the buttons to move through the storyline",
  stripRole: "Story filmstrip",
  prev: "Previous story card",
  next: "Next story card",
  cardPosition: "Card {n} of {total}",
  cards: [
    {
      step: "01",
      title: "The Global Crisis (Food Inflation)",
      body: "Worldwide food and energy inflation is squeezing household budgets. Traditional supply chains add predatory middleman markups to basic nutrition.",
      alt: "City skyline at dusk with rising cost-of-living graph overlay",
    },
    {
      step: "02",
      title: "Direct Sourcing (PlantyPando)",
      body: "Connecting organic farmers directly to local culinary hubs. Removing unnecessary markups ensures fair compensation for growers and stable wholesale costs.",
      alt: "High-tech automated greenhouse farm with direct produce dispatch",
    },
    {
      step: "03",
      title: "Decentralized Clean Power (SolarPando & AquaPando)",
      body: "Slashing kitchen utility bills through dedicated micro-solar grids and clean water filtration, making $1 production costs sustainably achievable.",
      alt: "Solar panels powering a modern commercial smart kitchen",
    },
    {
      step: "04",
      title: "Frictionless Utility (Meal2 $1 Voucher)",
      body: "Instant, sub-cent micro-transactions on Solana lock in meal purchasing power for everyday consumers anywhere.",
      alt: "Person paying for a meal bowl with a smartphone QR code scan",
    },
    {
      step: "05",
      title: "Infrastructure Expansion (Meal4 Governance)",
      body: "Empowering global Web3 investors to finance real-world physical infrastructure while participating in transparent protocol governance.",
      alt: "Futuristic dashboard showing real-world asset growth across pilot regions",
    },
  ],
  ctaTitle: "Join the Global Movement",
  ctaBody:
    "Two tokens, one mission: keep a healthy meal at $1 — and fund the infrastructure that makes it permanent.",
  ctaMeal2: "Buy Meal2",
  ctaMeal4: "Get Meal4",
  back: "Back to MealToken24",
};

const de: VisionCopy = {
  eyebrow: "Vision & Mission",
  headline: "Nahrungsmittelinflation weltweit einfrieren: Die 1\u00a0$-Mahlzeiten-Engine.",
  subheadline:
    "Wie MealToken24, Solana-Micropayments und nachhaltige Infrastruktur Gemeinschaften weltweit vor steigenden Lebenshaltungskosten schützen.",
  heroAlt:
    "Globale Netzwerkgrafik, die Smart Kitchens, Solaranlagen und nachhaltige Farmen über Kontinente verbindet",
  stripEyebrow: "Filmstreifen",
  stripTitle: "Die globale Storyline",
  stripHint: "Pfeiltasten oder Buttons nutzen, um durch die Storyline zu navigieren",
  stripRole: "Story-Filmstreifen",
  prev: "Vorherige Story-Karte",
  next: "Nächste Story-Karte",
  cardPosition: "Karte {n} von {total}",
  cards: [
    {
      step: "01",
      title: "Die globale Krise (Nahrungsmittelinflation)",
      body: "Weltweite Nahrungs- und Energieinflation belastet die Haushaltsbudgets. Klassische Lieferketten schlagen überzogene Zwischenhändler-Margen auf die Grundversorgung auf.",
      alt: "Skyline bei Dämmerung mit Grafik steigender Lebenshaltungskosten",
    },
    {
      step: "02",
      title: "Direkter Einkauf (PlantyPando)",
      body: "Bio-Landwirte werden direkt mit lokalen Küchen-Hubs verbunden. Ohne unnötige Aufschläge entstehen faire Erzeugerpreise und stabile Einkaufskosten.",
      alt: "Hochtechnologisches automatisiertes Gewächshaus mit Direktversand",
    },
    {
      step: "03",
      title: "Dezentrale saubere Energie (SolarPando & AquaPando)",
      body: "Micro-Solarnetze und Wasseraufbereitung senken die Betriebskosten der Küchen — so bleiben 1\u00a0$-Produktionskosten nachhaltig erreichbar.",
      alt: "Solarmodule versorgen eine moderne gewerbliche Smart Kitchen",
    },
    {
      step: "04",
      title: "Reibungslose Nutzung (Meal2 1\u00a0$-Gutschein)",
      body: "Sofortige Micro-Transaktionen im Sub-Cent-Bereich auf Solana sichern die Kaufkraft für Mahlzeiten — überall und für jeden.",
      alt: "Person bezahlt eine Mahlzeit per QR-Code-Scan mit dem Smartphone",
    },
    {
      step: "05",
      title: "Infrastruktur-Ausbau (Meal4 Governance)",
      body: "Web3-Investoren weltweit finanzieren reale physische Infrastruktur und nehmen an transparenter Protokoll-Governance teil.",
      alt: "Futuristisches Dashboard mit RWA-Wachstum in den Pilotregionen",
    },
  ],
  ctaTitle: "Werde Teil der globalen Bewegung",
  ctaBody:
    "Zwei Token, eine Mission: eine gesunde Mahlzeit für 1\u00a0$ — und die Infrastruktur, die das dauerhaft möglich macht.",
  ctaMeal2: "Meal2 kaufen",
  ctaMeal4: "Meal4 sichern",
  back: "Zurück zu MealToken24",
};

const zh: VisionCopy = {
  eyebrow: "愿景与使命",
  headline: "冻结全球食品通胀：1 美元餐食引擎。",
  subheadline:
    "MealToken24、Solana 微支付与可持续基础设施如何保护全球社区免受生活成本上涨的冲击。",
  heroAlt: "连接智能厨房、太阳能阵列与可持续农场的全球网络图",
  stripEyebrow: "影像卷",
  stripTitle: "全球故事线",
  stripHint: "使用方向键或按钮浏览故事线",
  stripRole: "故事影像卷",
  prev: "上一张故事卡",
  next: "下一张故事卡",
  cardPosition: "第 {n} 张，共 {total} 张",
  cards: [
    {
      step: "01",
      title: "全球危机（食品通胀）",
      body: "全球食品与能源通胀正在挤压家庭预算。传统供应链在基本营养上叠加了过高的中间商加价。",
      alt: "黄昏城市天际线与生活成本上升曲线叠加",
    },
    {
      step: "02",
      title: "直接采购（PlantyPando）",
      body: "将有机农户与本地餐饮枢纽直接连接。去除不必要的加价，让种植者获得公平回报，批发成本保持稳定。",
      alt: "高科技自动化温室农场直接发货",
    },
    {
      step: "03",
      title: "去中心化清洁能源（SolarPando 与 AquaPando）",
      body: "通过专属微型光伏电网与净水系统大幅降低厨房水电成本，使 1 美元的生产成本可持续实现。",
      alt: "太阳能板为现代商用智能厨房供电",
    },
    {
      step: "04",
      title: "无摩擦使用（Meal2 1 美元餐券）",
      body: "Solana 上的即时亚分级微交易，为世界各地的日常消费者锁定餐食购买力。",
      alt: "用户通过手机扫描二维码支付餐食",
    },
    {
      step: "05",
      title: "基础设施扩张（Meal4 治理）",
      body: "让全球 Web3 投资者为现实世界的物理基础设施提供资金，并参与透明的协议治理。",
      alt: "展示试点地区实体资产增长的未来感仪表板",
    },
  ],
  ctaTitle: "加入全球运动",
  ctaBody: "两种代币，一个使命：让健康餐食保持 1 美元，并为其长期存在提供基础设施资金。",
  ctaMeal2: "购买 Meal2",
  ctaMeal4: "获取 Meal4",
  back: "返回 MealToken24",
};

const ja: VisionCopy = {
  eyebrow: "ビジョンとミッション",
  headline: "世界の食料インフレを凍結する：1ドル・ミールエンジン。",
  subheadline:
    "MealToken24、Solana のマイクロペイメント、持続可能なインフラが、世界のコミュニティを生活費の高騰から守ります。",
  heroAlt: "スマートキッチン、太陽光アレイ、持続可能な農場を結ぶグローバルネットワーク図",
  stripEyebrow: "フィルムストリップ",
  stripTitle: "グローバル・ストーリーライン",
  stripHint: "矢印キーまたはボタンでストーリーを移動できます",
  stripRole: "ストーリー・フィルムストリップ",
  prev: "前のストーリーカード",
  next: "次のストーリーカード",
  cardPosition: "{total} 枚中 {n} 枚目",
  cards: [
    {
      step: "01",
      title: "世界的な危機（食料インフレ）",
      body: "世界的な食料・エネルギーインフレが家計を圧迫しています。従来のサプライチェーンは基本的な栄養に過剰な中間マージンを上乗せしています。",
      alt: "夕暮れの都市スカイラインと生活費上昇のグラフ",
    },
    {
      step: "02",
      title: "直接調達（PlantyPando）",
      body: "有機農家と地域の調理ハブを直接つなぎます。不要な上乗せをなくし、生産者への公正な対価と安定した仕入れコストを実現します。",
      alt: "ハイテク自動温室農場からの直接出荷",
    },
    {
      step: "03",
      title: "分散型クリーン電力（SolarPando と AquaPando）",
      body: "専用マイクロ太陽光グリッドと浄水により厨房の光熱費を大幅に削減し、1ドルの製造コストを持続的に実現します。",
      alt: "太陽光パネルが最新の業務用スマートキッチンに給電",
    },
    {
      step: "04",
      title: "摩擦のない利用（Meal2 1ドルバウチャー）",
      body: "Solana 上のサブセント級の即時マイクロ決済が、どこでも日常の購買力を固定します。",
      alt: "スマートフォンの QR コードで食事を支払う人",
    },
    {
      step: "05",
      title: "インフラ拡張（Meal4 ガバナンス）",
      body: "世界の Web3 投資家が現実の物理インフラに資金を提供し、透明なプロトコル・ガバナンスに参加します。",
      alt: "パイロット地域の実物資産の成長を示す近未来的ダッシュボード",
    },
  ],
  ctaTitle: "グローバルな動きに参加する",
  ctaBody:
    "2つのトークン、1つのミッション：健康的な食事を1ドルに保ち、それを恒久化するインフラに資金を供給します。",
  ctaMeal2: "Meal2 を購入",
  ctaMeal4: "Meal4 を取得",
  back: "MealToken24 に戻る",
};

const ko: VisionCopy = {
  eyebrow: "비전과 미션",
  headline: "전 세계 식품 인플레이션을 동결하다: 1달러 식사 엔진.",
  subheadline:
    "MealToken24, Solana 마이크로결제, 지속 가능한 인프라가 전 세계 지역사회를 생활비 상승으로부터 보호하는 방법.",
  heroAlt: "스마트 주방, 태양광 어레이, 지속 가능한 농장을 연결하는 글로벌 네트워크 그래픽",
  stripEyebrow: "필름스트립",
  stripTitle: "글로벌 스토리라인",
  stripHint: "방향키 또는 버튼으로 스토리라인을 이동하세요",
  stripRole: "스토리 필름스트립",
  prev: "이전 스토리 카드",
  next: "다음 스토리 카드",
  cardPosition: "{total}개 중 {n}번째 카드",
  cards: [
    {
      step: "01",
      title: "글로벌 위기 (식품 인플레이션)",
      body: "전 세계 식품·에너지 인플레이션이 가계 예산을 압박하고 있습니다. 기존 공급망은 기본 영양에 과도한 중간 마진을 더합니다.",
      alt: "황혼의 도시 스카이라인과 생활비 상승 그래프",
    },
    {
      step: "02",
      title: "직접 조달 (PlantyPando)",
      body: "유기농 농가와 지역 조리 허브를 직접 연결합니다. 불필요한 마진을 제거해 생산자에게 공정한 보상과 안정적인 원가를 제공합니다.",
      alt: "첨단 자동화 온실 농장의 직접 출하",
    },
    {
      step: "03",
      title: "분산형 청정 전력 (SolarPando & AquaPando)",
      body: "전용 마이크로 태양광 그리드와 정수 시스템으로 주방 공과금을 절감해 1달러 생산 원가를 지속 가능하게 만듭니다.",
      alt: "현대적 상업용 스마트 주방에 전력을 공급하는 태양광 패널",
    },
    {
      step: "04",
      title: "간편한 사용 (Meal2 1달러 바우처)",
      body: "Solana의 즉시 소액 결제가 어디서나 일상 소비자의 식사 구매력을 고정합니다.",
      alt: "스마트폰 QR 코드로 식사를 결제하는 사람",
    },
    {
      step: "05",
      title: "인프라 확장 (Meal4 거버넌스)",
      body: "전 세계 Web3 투자자가 실물 인프라에 자금을 지원하고 투명한 프로토콜 거버넌스에 참여합니다.",
      alt: "파일럿 지역의 실물 자산 성장을 보여주는 미래형 대시보드",
    },
  ],
  ctaTitle: "글로벌 운동에 참여하세요",
  ctaBody:
    "두 개의 토큰, 하나의 미션: 건강한 식사를 1달러로 유지하고 이를 영구화할 인프라에 자금을 지원합니다.",
  ctaMeal2: "Meal2 구매",
  ctaMeal4: "Meal4 받기",
  back: "MealToken24로 돌아가기",
};

const ru: VisionCopy = {
  eyebrow: "Видение и миссия",
  headline: "Заморозить продовольственную инфляцию во всём мире: движок питания за 1 $.",
  subheadline:
    "Как MealToken24, микроплатежи Solana и устойчивая инфраструктура защищают сообщества по всему миру от роста стоимости жизни.",
  heroAlt:
    "Графика глобальной сети, соединяющей умные кухни, солнечные станции и устойчивые фермы",
  stripEyebrow: "Кинолента",
  stripTitle: "Глобальная история",
  stripHint: "Используйте стрелки или кнопки для перемещения по истории",
  stripRole: "Кинолента истории",
  prev: "Предыдущая карточка",
  next: "Следующая карточка",
  cardPosition: "Карточка {n} из {total}",
  cards: [
    {
      step: "01",
      title: "Глобальный кризис (продовольственная инфляция)",
      body: "Мировая инфляция продуктов и энергии давит на семейные бюджеты. Традиционные цепочки поставок добавляют избыточные наценки посредников к базовому питанию.",
      alt: "Городской горизонт на закате с графиком роста стоимости жизни",
    },
    {
      step: "02",
      title: "Прямые закупки (PlantyPando)",
      body: "Органические фермеры напрямую подключаются к локальным кулинарным хабам. Без лишних наценок — справедливая оплата фермерам и стабильная себестоимость.",
      alt: "Высокотехнологичная автоматизированная теплица с прямой отгрузкой",
    },
    {
      step: "03",
      title: "Децентрализованная чистая энергия (SolarPando и AquaPando)",
      body: "Микросолнечные сети и очистка воды резко снижают коммунальные расходы кухонь, делая себестоимость в 1 $ устойчиво достижимой.",
      alt: "Солнечные панели питают современную коммерческую умную кухню",
    },
    {
      step: "04",
      title: "Простое использование (ваучер Meal2 за 1 $)",
      body: "Мгновенные микротранзакции на Solana фиксируют покупательную способность для повседневных потребителей в любой точке мира.",
      alt: "Человек оплачивает блюдо сканированием QR-кода со смартфона",
    },
    {
      step: "05",
      title: "Расширение инфраструктуры (управление Meal4)",
      body: "Web3-инвесторы со всего мира финансируют реальную физическую инфраструктуру и участвуют в прозрачном управлении протоколом.",
      alt: "Футуристичная панель с ростом реальных активов в пилотных регионах",
    },
  ],
  ctaTitle: "Присоединяйтесь к глобальному движению",
  ctaBody:
    "Два токена, одна миссия: сохранить здоровое питание за 1 $ и профинансировать инфраструктуру, которая делает это постоянным.",
  ctaMeal2: "Купить Meal2",
  ctaMeal4: "Получить Meal4",
  back: "Назад к MealToken24",
};

const pt: VisionCopy = {
  eyebrow: "Visão & Missão",
  headline: "Congelando a inflação alimentar no mundo: o motor da refeição de US$ 1.",
  subheadline:
    "Como a MealToken24, os micropagamentos na Solana e a infraestrutura sustentável protegem comunidades globais do aumento do custo de vida.",
  heroAlt:
    "Gráfico de rede global conectando cozinhas inteligentes, usinas solares e fazendas sustentáveis",
  stripEyebrow: "Filmstrip",
  stripTitle: "A Narrativa Global",
  stripHint: "Use as setas do teclado ou os botões para percorrer a narrativa",
  stripRole: "Filmstrip da narrativa",
  prev: "Cartão anterior",
  next: "Próximo cartão",
  cardPosition: "Cartão {n} de {total}",
  cards: [
    {
      step: "01",
      title: "A crise global (inflação alimentar)",
      body: "A inflação mundial de alimentos e energia aperta o orçamento das famílias. Cadeias tradicionais somam margens abusivas de intermediários à nutrição básica.",
      alt: "Skyline urbana ao entardecer com gráfico de custo de vida em alta",
    },
    {
      step: "02",
      title: "Compra direta (PlantyPando)",
      body: "Conectamos produtores orgânicos diretamente aos hubs culinários locais. Sem margens desnecessárias, há remuneração justa e custo estável.",
      alt: "Estufa automatizada de alta tecnologia com despacho direto",
    },
    {
      step: "03",
      title: "Energia limpa descentralizada (SolarPando & AquaPando)",
      body: "Microrredes solares e filtragem de água reduzem as contas das cozinhas, tornando o custo de produção de US$ 1 sustentável.",
      alt: "Painéis solares alimentando uma cozinha comercial inteligente",
    },
    {
      step: "04",
      title: "Utilidade sem atrito (voucher Meal2 de US$ 1)",
      body: "Microtransações instantâneas na Solana travam o poder de compra de refeições para consumidores em qualquer lugar.",
      alt: "Pessoa pagando uma refeição via QR code no smartphone",
    },
    {
      step: "05",
      title: "Expansão da infraestrutura (governança Meal4)",
      body: "Investidores Web3 globais financiam infraestrutura física real e participam de uma governança de protocolo transparente.",
      alt: "Painel futurista mostrando o crescimento de ativos reais nas regiões piloto",
    },
  ],
  ctaTitle: "Faça parte do movimento global",
  ctaBody:
    "Dois tokens, uma missão: manter uma refeição saudável por US$ 1 — e financiar a infraestrutura que torna isso permanente.",
  ctaMeal2: "Comprar Meal2",
  ctaMeal4: "Obter Meal4",
  back: "Voltar para a MealToken24",
};

const BY_LANG: Record<Lang, VisionCopy> = { en, de, zh, ja, ko, ru, pt };

export function visionCopy(lang: Lang): VisionCopy {
  return BY_LANG[lang] ?? en;
}
